(() => {
  const formatPage = (value) => String(Math.max(1, value)).padStart(2, '0');

  class SopapetsEditorialShowcase {
    constructor(root) {
      if (!root || root.dataset.spxInitialized === 'true') return;

      root.dataset.spxInitialized = 'true';
      root.spxShowcaseInstance = this;
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll('[data-spx-tab]'));
      this.panels = Array.from(root.querySelectorAll('[data-spx-panel]'));
      this.observers = [];
      this.scrollFrames = new WeakMap();
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.spxTab));
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index));
      });

      this.panels.forEach((panel) => this.setupPanel(panel));
      this.syncVisiblePanel();
    }

    setupPanel(panel) {
      const rail = panel.querySelector('[data-spx-rail]');
      const previous = panel.querySelector('[data-spx-prev]');
      const next = panel.querySelector('[data-spx-next]');

      if (!rail || !previous || !next) return;

      previous.addEventListener('click', () => this.move(panel, -1));
      next.addEventListener('click', () => this.move(panel, 1));
      rail.addEventListener('scroll', () => this.requestSync(panel), { passive: true });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => this.requestSync(panel));
        observer.observe(rail);
        this.observers.push(observer);
      } else {
        window.addEventListener('resize', () => this.requestSync(panel), { passive: true });
      }
    }

    handleTabKeydown(event, index) {
      if (!this.tabs.length) return;

      let targetIndex = null;
      if (event.key === 'ArrowRight') targetIndex = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') targetIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = this.tabs.length - 1;

      if (targetIndex === null) return;

      event.preventDefault();
      const targetTab = this.tabs[targetIndex];
      this.activate(targetTab.dataset.spxTab);
      targetTab.focus();
    }

    activate(id) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.spxTab === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const active = panel.dataset.spxPanel === id;
        panel.hidden = !active;

        if (active) {
          const rail = panel.querySelector('[data-spx-rail]');
          if (rail) rail.scrollTo({ left: 0, behavior: 'auto' });
          requestAnimationFrame(() => this.syncPanel(panel));
        }
      });
    }

    selectBlock(target) {
      const panel = target.closest('[data-spx-panel]');
      if (panel) this.activate(panel.dataset.spxPanel);
    }

    requestSync(panel) {
      if (this.scrollFrames.get(panel)) return;

      const frame = requestAnimationFrame(() => {
        this.scrollFrames.delete(panel);
        this.syncPanel(panel);
      });

      this.scrollFrames.set(panel, frame);
    }

    getMetrics(panel) {
      const rail = panel.querySelector('[data-spx-rail]');
      const cards = rail ? Array.from(rail.querySelectorAll('[data-spx-product]')) : [];
      const firstCard = cards[0];

      if (!rail || !firstCard) {
        return {
          rail,
          cards,
          step: 0,
          totalPages: 1,
          currentPage: 1,
          maxScroll: 0,
        };
      }

      const styles = window.getComputedStyle(rail);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const cardWidth = firstCard.getBoundingClientRect().width;
      const perPage = Math.max(1, Math.round((rail.clientWidth + gap) / (cardWidth + gap)));
      const step = Math.max(1, (cardWidth + gap) * perPage);
      const totalPages = Math.max(1, Math.ceil(cards.length / perPage));
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const currentPage = maxScroll <= 2
        ? 1
        : Math.min(totalPages, Math.max(1, Math.round(rail.scrollLeft / step) + 1));

      return {
        rail,
        cards,
        step,
        totalPages,
        currentPage,
        maxScroll,
      };
    }

    move(panel, direction) {
      const metrics = this.getMetrics(panel);
      if (!metrics.rail || metrics.maxScroll <= 2) return;

      const targetPage = Math.min(
        metrics.totalPages,
        Math.max(1, metrics.currentPage + direction),
      );
      const targetLeft = Math.min(metrics.maxScroll, (targetPage - 1) * metrics.step);

      metrics.rail.scrollTo({
        left: targetLeft,
        behavior: this.reduceMotion ? 'auto' : 'smooth',
      });
    }

    syncPanel(panel) {
      if (!panel || panel.hidden) return;

      const metrics = this.getMetrics(panel);
      if (!metrics.rail) return;

      const previous = panel.querySelector('[data-spx-prev]');
      const next = panel.querySelector('[data-spx-next]');
      const current = panel.querySelector('[data-spx-current-page]');
      const total = panel.querySelector('[data-spx-total-pages]');
      const progress = panel.querySelector('[data-spx-progress]');
      const hasOverflow = metrics.maxScroll > 2;

      panel.classList.toggle('is-overflowing', hasOverflow);
      previous.disabled = !hasOverflow || metrics.rail.scrollLeft <= 2;
      next.disabled = !hasOverflow || metrics.maxScroll - metrics.rail.scrollLeft <= 2;

      if (current) current.textContent = formatPage(metrics.currentPage);
      if (total) total.textContent = formatPage(metrics.totalPages);
      if (progress) progress.style.transform = `scaleX(${metrics.currentPage / metrics.totalPages})`;
    }

    syncVisiblePanel() {
      const visiblePanel = this.panels.find((panel) => !panel.hidden) || this.panels[0];
      if (visiblePanel) requestAnimationFrame(() => this.syncPanel(visiblePanel));
    }

    destroy() {
      this.observers.forEach((observer) => observer.disconnect());
      this.root.dataset.spxInitialized = 'false';
      delete this.root.spxShowcaseInstance;
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches && scope.matches('[data-spx-showcase]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-spx-showcase]'));
    roots.forEach((root) => new SopapetsEditorialShowcase(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-spx-showcase]')
      ? event.target
      : event.target.querySelector?.('[data-spx-showcase]');
    root?.spxShowcaseInstance?.destroy();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-spx-showcase]');
    root?.spxShowcaseInstance?.selectBlock(event.target);
  });
})();
